import React, { useState, useEffect } from 'react';
import { formatEventDate, formatEventTime, buildEventDateTime } from "../../helpers/eventDateTime";
import { useNavigate, useSearchParams } from 'react-router-dom';
import API_UTIL from '../../services/AuthIntereptor';
import { toast } from 'react-toastify';
import AppBar from '../../components/AppBar/AppBar';
import Footer from '../../components/Footer/Footer';
import LoadingSpinner from '../../components/Loader/LoadingSpinner';
import {
  Button,
  Input,
  Select,
  Textarea,
  FileInput,
  UnderlineTab,
  EventCover,
  ModalShell,
  ModalHeader,
  Icons,
} from '../../components/ui';

/* ---------- Create card ---------- */
const CreateCard = ({ onClick }) => (
  <button
    onClick={onClick}
    className="group rounded-xl border-2 border-dashed border-gray-300 hover:border-brand-500 hover:bg-brand-50/30 transition flex flex-col items-center justify-center min-h-[280px] p-6 text-slate-500 hover:text-brand-600"
  >
    <span className="w-14 h-14 rounded-full bg-brand-50 border border-brand-100 grid place-items-center text-brand-600 group-hover:bg-brand-600 group-hover:text-white transition">
      <Icons.Plus className="w-6 h-6" />
    </span>
    <span className="mt-3 font-semibold text-slate-700 group-hover:text-brand-700">Create New Event</span>
    <span className="text-xs text-slate-400 mt-1 max-w-[18ch] text-center leading-relaxed">
      Add a new event, generate its QR, and invite guests.
    </span>
  </button>
);

/* ---------- Event card ---------- */
const EventCardItem = ({ event, mode, onClick, onDelete, formatDate, formatTime }) => {
  const dateStr = formatDate(event);
  const timeStr = formatTime(event);
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-card-hover hover:-translate-y-1 transition-all"
    >
      <div className="relative aspect-[16/10] bg-gray-100">
        <EventCover src={event.event_image || undefined} label={(event.event_name || '').replace(/_/g, ' ')} />
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute top-2 right-2 w-8 h-8 grid place-items-center bg-white/90 backdrop-blur border border-gray-200 text-slate-500 hover:text-red-600 hover:border-red-200 rounded-lg shadow-soft opacity-0 group-hover:opacity-100 transition"
            aria-label="Delete event"
            title="Delete event"
          >
            <Icons.Trash className="w-4 h-4" />
          </button>
        )}
        {mode === 'attended' && (
          <span className="absolute top-2 left-2 bg-white/90 backdrop-blur border border-gray-200 text-[10px] font-semibold tracking-wider text-slate-700 uppercase px-2 py-0.5 rounded">
            Attended
          </span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-slate-900 text-sm leading-tight truncate" title={(event.event_name || '').replace(/_/g, ' ')}>
          {(event.event_name || '').replace(/_/g, ' ')}
        </h3>
        <div className="mt-3 space-y-1.5 text-xs text-slate-500">
          {dateStr && (
            <div className="flex items-center gap-1.5">
              <Icons.Calendar className="w-3.5 h-3.5 text-brand-600" />
              <span>{dateStr}</span>
            </div>
          )}
          {timeStr && (
            <div className="flex items-center gap-1.5">
              <Icons.Clock className="w-3.5 h-3.5 text-brand-600" />
              <span>{timeStr}</span>
            </div>
          )}
          {event.event_location && (
            <div className="flex items-start gap-1.5">
              <Icons.Pin className="w-3.5 h-3.5 text-brand-600 mt-0.5 shrink-0" />
              <span className="line-clamp-2 leading-snug">{event.event_location}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Event = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [attendedEvents, setAttendedEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUserDetailsModalOpen, setIsUserDetailsModalOpen] = useState(false);
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [displayNone, setDisplayNone] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'my' ? 'myFlashbacks' : 'attendedFlashbacks'
  );
  const [eventToDelete, setEventToDelete] = useState(null);
  const [watermarkPreview, setWatermarkPreview] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [isLoadingAttendedEvents, setIsLoadingAttendedEvents] = useState(false);
  const [query, setQuery] = useState('');
  const userPhoneNumber = localStorage.userPhoneNumber;

  const [formData, setFormData] = useState({
    eventName: '',
    eventStartDate: '',
    eventEndDate: '',
    eventTime: '',
    eventLocation: '',
    projectName: '',
    invitationNote: '',
    eventImage: null,
    watermarkImage: null,
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [userFormData, setUserFormData] = useState({
    user_name: '',
    social_media: { instagram: '', youtube: '' },
    org_name: '',
    role: 'creator',
  });
  const [newProjectName, setNewProjectName] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleWatermarkChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, watermarkImage: file });
      const reader = new FileReader();
      reader.onload = (ev) => setWatermarkPreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const loadDefaultImage = async () => {
      try {
        const response = await fetch('assets/Images/event-card.jpg');
        const blob = await response.blob();
        const defaultImageFile = new File([blob], 'event-card.jpg', { type: 'image/jpeg' });
        setSelectedFile(defaultImageFile);
      } catch (err) {
        console.error('Error loading default image:', err);
      }
    };
    loadDefaultImage();
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      const phone = localStorage.getItem('userPhoneNumber');
      if (!phone) {
        navigate('/login');
        return;
      }
      try {
        const userResponse = await API_UTIL.get(`/fetchUserDetails/${phone}`);
        const userData = userResponse.data.data;
        setUserDetails(userData);
        setUserRole(userData.role || 'user');

        if (userData.role === 'creator') {
          const [eventsResponse, projectsResponse] = await Promise.all([
            API_UTIL.get(`/getClientEventDetails/${userData.user_name}`),
            API_UTIL.get(`/getProjectDetails/${userData.user_name}`),
          ]);
          setEvents(eventsResponse.data);
          setProjects(projectsResponse.data);
        }

        const attendedResponse = await API_UTIL.get(`/getUserAttendedEvents/${phone}`);
        setAttendedEvents(attendedResponse.data);
      } catch (err) {
        console.error('Error initializing:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, [navigate]);

  useEffect(() => {
    const nextTab = searchParams.get('tab') === 'my' ? 'myFlashbacks' : 'attendedFlashbacks';

    if (userRole && userRole !== 'creator' && nextTab === 'myFlashbacks') {
      setSearchParams({ tab: 'attended' }, { replace: true });
      if (selectedTab !== 'attendedFlashbacks') setSelectedTab('attendedFlashbacks');
      return;
    }

    if (selectedTab !== nextTab) {
      setSelectedTab(nextTab);
    }
  }, [searchParams, selectedTab, setSearchParams, userRole]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSearchParams({ tab: tab === 'myFlashbacks' ? 'my' : 'attended' }, { replace: true });
  };

  const getEventReturnTo = (tab = selectedTab) => `/event?tab=${tab === 'myFlashbacks' ? 'my' : 'attended'}`;

  useEffect(() => {
    const fetchProjects = async (clientName) => {
      try {
        const response = await API_UTIL.get(`/getProjectDetails/${clientName}`);
        setProjects(response.status === 200 ? response.data : []);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setProjects([]);
      }
    };

    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        const response = await API_UTIL.get(`/fetchUserDetails/${userPhoneNumber}`);
        setUserDetails(response.data.data);
        fetchEventData(response.data.data.user_name);
        fetchProjects(response.data.data.user_name);
        setTimeout(() => setLoading(false), 250);
      } catch (err) {
        console.error('Error fetching user details:', err);
        setTimeout(() => setLoading(false), 250);
      }
    };

    fetchUserDetails();
  }, [userPhoneNumber]);


  const fetchAttendedEvents = async () => {
    setIsLoadingAttendedEvents(true);
    try {
      const attendedResponse = await API_UTIL.get(`/getUserAttendedEvents/${userPhoneNumber}`);
      setAttendedEvents(attendedResponse.data);
    } catch (err) {
      console.error('Error fetching attended events:', err);
      toast.error('Failed to load attended events. Please try again.');
      setAttendedEvents([]);
    } finally {
      setIsLoadingAttendedEvents(false);
    }
  };

  useEffect(() => {
    if (selectedTab === 'attendedFlashbacks') {
      fetchAttendedEvents();
    }
  }, [selectedTab]);

  const fetchEventData = async (userName) => {
    try {
      const response = await API_UTIL.get(`/getClientEventDetails/${userName}`);
      const collabResponse = await API_UTIL.get(`/getCollabEvents/${userName}`);
      const allEvents = [...response.data, ...collabResponse.data];
      const uniqueEvents = allEvents.reduce((acc, current) => {
        const x = acc.find((item) => item.event_id === current.event_id);
        return x ? acc : acc.concat([current]);
      }, []);
      setEvents(uniqueEvents);

      const attendedResponse = await API_UTIL.get(`/getUserAttendedEvents/${userPhoneNumber}`);
      setAttendedEvents(attendedResponse.data);
    } catch (err) {
      setError(err.message);
      throw Error('Error in fetching Events info');
    }
  };

  const onEventClick = (event_id) => {
    navigate(`/eventDetails/${event_id}`, { state: { userDetails, returnTo: getEventReturnTo('myFlashbacks') } });
  };

  const onAttendEventClick = (event_id) => {
    navigate(`/photosV1/${event_id}/${userDetails.user_id}`, {
      state: { userDetails, returnTo: getEventReturnTo('attendedFlashbacks') },
    });
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };

  const openCreateEventModal = () => {
    if (userDetails.user_name === userPhoneNumber) {
      setIsUserDetailsModalOpen(true);
    } else {
      setIsCreateModalOpen(true);
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      setDeleteMessage('Deleting event...');
      await API_UTIL.delete(`/deleteEvent/${eventId}/${userPhoneNumber}`);
      setEvents(events.filter((event) => event.event_id !== eventId));
      setDeleteMessage('Delete successful');
      setShowSuccessAnimation(true);
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        setDeleteMessage('');
        setShowSuccessAnimation(false);
      }, 2000);
    } catch (err) {
      console.error('Error deleting event:', err);
      setDeleteMessage('Failed to delete the event. Please try again.');
      toast.error('Failed to delete the event. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const endDate = formData.eventEndDate || formData.eventStartDate;
    // The time is optional — an event with no time is stored as a plain date.
    const combinedStartDateTime = buildEventDateTime(formData.eventStartDate, formData.eventTime);
    const combinedEndDateTime = buildEventDateTime(endDate, formData.eventTime);

    setUploading(true);

    const formDataToSend = new FormData();
    formDataToSend.append('eventName', formData.eventName);
    formDataToSend.append('eventStartDate', combinedStartDateTime);
    formDataToSend.append('eventEndDate', combinedEndDateTime);
    formDataToSend.append('eventLocation', formData.eventLocation);
    formDataToSend.append('projectName', formData.projectName);
    formDataToSend.append('clientName', userDetails.user_name);
    formDataToSend.append('invitationNote', formData.invitationNote);
    if (selectedFile) {
      formDataToSend.append('eventImage', selectedFile);
    }
    if (formData.watermarkImage) {
      formDataToSend.append('watermarkImage', formData.watermarkImage);
    }

    try {
      const response = await API_UTIL.post('/saveEventDetails', formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.status !== 200) {
        throw new Error('Failed to save the event');
      }
      setEvents([...events, response.data.data]);
      setIsCreateModalOpen(false);
      setFormData({
        eventName: '',
        eventStartDate: '',
        eventEndDate: '',
        eventTime: '',
        eventLocation: '',
        projectName: '',
        invitationNote: '',
        eventImage: null,
      });
      setWatermarkPreview(null);
      return response;
    } catch (err) {
      console.error('Error saving form data to backend:', err);
      toast.error('Failed to save the events. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleNewProjectChange = (e) => {
    setNewProjectName(e.target.value);
  };

  const handleCreateProject = async () => {
    if (!newProjectName) {
      toast.error('Please enter a project name.');
      return;
    }
    try {
      const response = await API_UTIL.post('/saveProjectDetails', {
        projectName: newProjectName,
        clientName: userDetails.user_name,
      });
      setProjects([response.data.data, ...projects]);
      setFormData({ ...formData, projectName: response.data.data.project });
      setNewProjectName('');
      setShowNewProjectInput(false);
      toast.success('Project created successfully');
    } catch (err) {
      console.error('Error creating project:', err);
      toast.error('Failed to create project. Please try again.');
    } finally {
      setDisplayNone(!displayNone);
    }
  };

  const handleUserDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      const requestData = { user_phone_number: userPhoneNumber, ...userFormData };
      const res = await API_UTIL.post('/updatePortfolioDetails', requestData);
      if (res.status === 200) {
        setUserDetails(res.data.data);
        setIsUserDetailsModalOpen(false);
        setIsCreateModalOpen(true);
      }
    } catch (err) {
      console.error('Error in registering the model:', err);
      if (err.response) {
        toast.error('Error in creating Model');
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error}</div>;

  const isMyTab = userRole === 'creator' && selectedTab === 'myFlashbacks';
  const list = isMyTab ? events : attendedEvents;
  const filtered = list.filter(
    (e) =>
      (e.event_name || '').toLowerCase().includes(query.toLowerCase()) ||
      (e.event_location || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <AppBar />

      <div className="flex-1 fade-in max-w-[1400px] mx-auto w-full px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Events</h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage events you're hosting and the ones you've attended.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search events…"
                className="pl-9 pr-3 py-2.5 w-full sm:w-64 bg-white border border-gray-200 rounded-lg text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30"
              />
            </div>
            {userRole === 'creator' && selectedTab === 'myFlashbacks' && (
              <Button onClick={openCreateEventModal} icon={Icons.Plus} className="whitespace-nowrap">
                Create event
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 flex items-center gap-8">
          {userRole === 'creator' && (
            <UnderlineTab
              active={selectedTab === 'myFlashbacks'}
              onClick={() => handleTabChange('myFlashbacks')}
              count={events.length}
            >
              My Events
            </UnderlineTab>
          )}
          <UnderlineTab
            active={selectedTab === 'attendedFlashbacks'}
            onClick={() => handleTabChange('attendedFlashbacks')}
            count={attendedEvents.length}
          >
            Attended Events
          </UnderlineTab>
        </div>

        {/* Grid */}
        {isLoadingAttendedEvents && !isMyTab ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {isMyTab && <CreateCard onClick={openCreateEventModal} />}
            {filtered.map((ev) => (
              <EventCardItem
                key={ev.event_id}
                event={ev}
                mode={isMyTab ? 'my' : 'attended'}
                formatDate={formatEventDate}
                formatTime={formatEventTime}
                onClick={() => (isMyTab ? onEventClick(ev.event_id) : onAttendEventClick(ev.folder_name))}
                onDelete={
                  isMyTab
                    ? () => {
                        setEventToDelete(ev);
                        setIsDeleteModalOpen(true);
                      }
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {filtered.length === 0 && !isMyTab && (
          <div className="text-center text-slate-400 text-sm py-16">No attended events match your search.</div>
        )}
      </div>

      {/* ---------- Modals (creator) ---------- */}
      {userRole === 'creator' && (
        <>
          {/* Profile setup modal */}
          <ModalShell open={isUserDetailsModalOpen} onClose={() => setIsUserDetailsModalOpen(false)} maxWidth="max-w-lg">
            <ModalHeader
              title="Set up your profile"
              subtitle="Add your brand details to start creating events."
              onClose={() => setIsUserDetailsModalOpen(false)}
            />
            <form onSubmit={handleUserDetailsSubmit} className="p-6 space-y-5">
              <Input
                label="Brand Name"
                value={userFormData.org_name}
                onChange={(e) => setUserFormData({ ...userFormData, org_name: e.target.value })}
                placeholder="e.g. The Stevie® Awards"
                required
              />
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Instagram URL</label>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
                  <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-slate-400">
                    <Icons.Instagram className="w-4 h-4" />
                  </span>
                  <input
                    type="url"
                    value={userFormData.social_media.instagram}
                    onChange={(e) =>
                      setUserFormData({
                        ...userFormData,
                        social_media: { ...userFormData.social_media, instagram: e.target.value },
                      })
                    }
                    placeholder="https://instagram.com/yourhandle"
                    className="flex-1 bg-white text-slate-900 placeholder-slate-400 text-sm px-3 py-2.5 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">YouTube URL</label>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30">
                  <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-slate-400">
                    <Icons.Youtube className="w-4 h-4" />
                  </span>
                  <input
                    type="url"
                    value={userFormData.social_media.youtube}
                    onChange={(e) =>
                      setUserFormData({
                        ...userFormData,
                        social_media: { ...userFormData.social_media, youtube: e.target.value },
                      })
                    }
                    placeholder="https://youtube.com/@yourchannel"
                    className="flex-1 bg-white text-slate-900 placeholder-slate-400 text-sm px-3 py-2.5 focus:outline-none"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full">
                Save
              </Button>
            </form>
          </ModalShell>

          {/* Delete confirmation modal */}
          <ModalShell open={isDeleteModalOpen} onClose={closeDeleteModal} maxWidth="max-w-md">
            <ModalHeader title="Delete event" subtitle={(eventToDelete?.event_name || '').replace(/_/g, ' ')} onClose={closeDeleteModal} />
            <div className="p-6">
              {deleteMessage ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-700">{deleteMessage}</p>
                  {showSuccessAnimation && (
                    <div className="mt-4 flex justify-center">
                      <div className="w-12 h-12 rounded-full bg-brand-50 border border-brand-100 grid place-items-center text-brand-600 scale-in">
                        <Icons.Check className="w-6 h-6" />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-600">
                    Are you sure you want to delete this event? This cannot be undone.
                  </p>
                  <div className="mt-5 flex items-center justify-end gap-2">
                    <Button variant="secondary" onClick={closeDeleteModal}>
                      Cancel
                    </Button>
                    <Button variant="danger" onClick={() => deleteEvent(eventToDelete.event_id)}>
                      Delete
                    </Button>
                  </div>
                </>
              )}
            </div>
          </ModalShell>

          {/* Create event modal */}
          <ModalShell open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
            <ModalHeader
              title="Create Event"
              subtitle="Set the basics. You can edit anything later."
              onClose={() => setIsCreateModalOpen(false)}
            />
            <form onSubmit={handleCreateEvent} className="p-6 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="Event Name"
                  name="eventName"
                  value={formData.eventName}
                  onChange={handleInputChange}
                  placeholder="e.g. 2026 Stevie Awards"
                  required
                />
                <Input
                  label="Start Date"
                  type="date"
                  name="eventStartDate"
                  value={formData.eventStartDate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input
                  label="End Date (Optional)"
                  type="date"
                  name="eventEndDate"
                  value={formData.eventEndDate}
                  onChange={handleInputChange}
                  min={formData.eventStartDate}
                />
                <Input
                  label="Event Time"
                  type="time"
                  name="eventTime"
                  value={formData.eventTime}
                  onChange={handleInputChange}
                />
              </div>
              <Input
                label="Event Location"
                name="eventLocation"
                value={formData.eventLocation}
                onChange={handleInputChange}
                placeholder="Venue, city"
              />
              <div className="grid sm:grid-cols-2 gap-4 items-end">
                <Select
                  label="Project Name"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleInputChange}
                  disabled={showNewProjectInput}
                  required
                >
                  <option value="">Select Project</option>
                  {projects.map((project, index) => (
                    <option key={index} value={project}>
                      {project}
                    </option>
                  ))}
                </Select>
                <div className="flex items-center gap-2 text-sm pb-2.5">
                  <span className="text-slate-500">or</span>
                  <button
                    type="button"
                    onClick={() => setShowNewProjectInput(!showNewProjectInput)}
                    className="text-brand-600 font-semibold hover:underline inline-flex items-center gap-1"
                  >
                    <Icons.Plus className="w-4 h-4" />
                    {showNewProjectInput ? 'Use existing project' : 'Create new project'}
                  </button>
                </div>
              </div>

              {showNewProjectInput && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
                  <div className="flex-1">
                    <Input
                      label="New project name"
                      value={newProjectName}
                      onChange={handleNewProjectChange}
                      placeholder="e.g. Spring Galas 2026"
                    />
                  </div>
                  <Button variant="secondary" type="button" icon={Icons.Plus} onClick={handleCreateProject}>
                    Add Project
                  </Button>
                </div>
              )}

              <Textarea
                label="Invitation Note"
                name="invitationNote"
                value={formData.invitationNote}
                onChange={handleInputChange}
                placeholder="A short message your attendees will see when they scan the QR code."
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <FileInput label="Upload Image" onChange={handleFileChange} />
                  <p className="text-[11px] text-slate-400 mt-1.5">PNG or JPG, max 5 MB</p>
                </div>
                <div>
                  <FileInput label="Upload Watermark (Optional)" onChange={handleWatermarkChange} />
                  {watermarkPreview && (
                    <img
                      src={watermarkPreview}
                      alt="Watermark preview"
                      className="mt-2 max-w-[100px] max-h-[50px] object-contain"
                    />
                  )}
                  <p className="text-[11px] text-slate-400 mt-1.5">Transparent PNG recommended</p>
                </div>
              </div>

              <div className="pt-2">
                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? 'Creating…' : 'Create Event'}
                </Button>
              </div>
            </form>
          </ModalShell>
        </>
      )}

      <Footer />
    </div>
  );
};

export default Event;
